(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root && root.document) api.start(root);
})(typeof window === 'undefined' ? null : window, function () {
  'use strict';
  const storageKey = 'pc-navigation-context-v2';
  const intentLifetime = 90000;

  function parseURL(value, origin) {
    try {
      const url = new URL(value, origin);
      return /^https?:$/.test(url.protocol) ? url : null;
    } catch (_) { return null; }
  }

  function pathKey(value, origin) {
    const url = parseURL(value, origin);
    if (!url) return '';
    let path;
    try { path = decodeURIComponent(url.pathname); } catch (_) { path = url.pathname; }
    return path.replace(/^\/(?:en|fr)(?:-[a-z]{2})?(?=\/|$)/i, '').replace(/\/+$/, '') || '/';
  }

  function isStoreURL(url, config, origin) {
    if (!url) return false;
    const host = url.hostname.replace(/^www\./, '').toLowerCase();
    return [new URL(origin).hostname, ...(config.hosts || [])].some(value =>
      typeof value === 'string' && value.replace(/^www\./, '').toLowerCase() === host
    );
  }

  function explicitSite(value, config, origin) {
    const url = parseURL(value, origin);
    if (!isStoreURL(url, config, origin)) return '';
    const key = pathKey(url.href, origin);
    for (const [site, home] of Object.entries(config.sites)) {
      if (key === pathKey(home.url, origin)) return site;
    }
    if (key === '/search') {
      const profile = url.searchParams.get('floreal_profile');
      if (profile === 'noel') return 'noel';
      if (profile === 'jardinage') return 'pepiniere';
    }
    const plain = key.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    if (/(?:^|[\/-])(?:noel|christmas)(?:[\/-]|$)/.test(plain)) return 'noel';
    if (/^\/(?:pages|collections)\//.test(plain)) {
      if (/(?:^|[\/-])brossard(?:[\/-]|$)/.test(plain)) return 'brossard';
      if (/(?:^|[\/-])floreal(?:[\/-]|$)/.test(plain)) return 'floreal';
    }
    return '';
  }

  function validState(state, config, now, ttl) {
    return !!(state && state.version === 2 && config.sites[state.site] &&
      typeof state.updatedAt === 'number' && now >= state.updatedAt && now - state.updatedAt <= ttl);
  }

  function resolve(config, data, location, referrer, state, now, sessionEnabled) {
    const origin = location.origin;
    const current = pathKey(location.href, origin);
    const previousURL = parseURL(referrer, origin);
    const internalReferrer = !!referrer && isStoreURL(previousURL, config, origin);
    const previous = internalReferrer ? pathKey(previousURL.href, origin) : '';
    const ttl = Math.max(15, Math.min(Number(data.minutes) || 240, 720)) * 60000;
    const remembered = sessionEnabled && validState(state, config, now, ttl) ? state : null;
    const pending = remembered && remembered.intent;
    const intent = pending && now - pending.at >= 0 && now - pending.at < intentLifetime &&
      pending.to === current && (!previous || pending.from === previous) ? pending : null;
    const explicit = explicitSite(location.href, config, origin) || config.explicitSite || '';
    const sameResource = remembered && internalReferrer && config.resourceId &&
      remembered.resourceId === config.resourceId && remembered.pageType === config.pageType &&
      previous === remembered.path;
    let site = explicit;
    if (!site && intent && config.sites[intent.site]) site = intent.site;
    if (!site && sessionEnabled && config.sites[data.documentSite]) site = data.documentSite;
    if (!site && remembered && previous && remembered.path === previous) site = remembered.site;
    if (!site && internalReferrer) site = explicitSite(previousURL.href, config, origin);

    const members = Array.isArray(data.productCollections) ? data.productCollections : [];
    const allowed = item => item && !item.excluded && item.handle && item.url &&
      isStoreURL(parseURL(item.url, origin), config, origin);
    const member = key => key ? members.find(item => (item.handle === key || item.id === key || pathKey(item.url, origin) === key) && allowed(item)) : null;
    let collection = data.collection || null;
    let parent = data.parent || null;
    if (config.pageType === 'product') {
      const explicitMatch = current.match(/^\/collections\/([^/]+)\/products\//);
      const referringMatch = previous.match(/^\/collections\/([^/]+)$/);
      collection = member(explicitMatch && explicitMatch[1]) ||
        member(referringMatch && referringMatch[1]) ||
        member(intent && intent.collectionId) || member(intent && intent.collection) ||
        member(sameResource && remembered.collectionId) || member(data.documentCollection) ||
        member(data.collection && data.collection.id) || member(data.collection && data.collection.handle) || null;
      parent = collection && collection.parent || null;
    }
    if (!site && collection && config.sites[collection.site]) site = collection.site;
    if (!config.sites[site]) site = 'pepiniere';
    const home = config.sites[site];
    const isHome = current === pathKey(home.url, origin);
    const crumbs = [{ title: home.label, url: home.url }];
    const append = item => {
      if (!item || !item.title || !item.url || !isStoreURL(parseURL(item.url, origin), config, origin)) return;
      const key = pathKey(item.url, origin);
      if (key === current || crumbs.some(crumb => pathKey(crumb.url, origin) === key)) return;
      crumbs.push({ title: item.title, url: item.url });
    };
    if (!isHome) {
      append(parent);
      if (config.pageType === 'product') append(collection);
      if (config.pageType === 'article') append(data.blog);
      if (data.current && data.current.title) crumbs.push({ title: data.current.title, url: location.pathname, current: true });
    } else { crumbs[0].current = true; }
    return { site, collection, crumbs, isHome, path: current };
  }

  function start(win) {
    if (win.PepiniereBreadcrumb) { win.PepiniereBreadcrumb.refresh(); return; }
    const doc = win.document;
    let config;
    let active;
    let currentData = {};
    let enabled = true;
    function readJSON(element) {
      try { return element ? JSON.parse(element.textContent) : null; } catch (_) { return null; }
    }
    function readState() {
      try { return JSON.parse(win.sessionStorage.getItem(storageKey)); } catch (_) { return null; }
    }
    function writeState(state) {
      if (!enabled) return;
      try { win.sessionStorage.setItem(storageKey, JSON.stringify(state)); } catch (_) { }
    }
    function localURL(value) {
      const url = parseURL(value, win.location.origin);
      return url && isStoreURL(url, config, win.location.origin) ? url.pathname + url.search + url.hash : '';
    }
    function render(nav, result) {
      const list = nav.querySelector('ol');
      if (!list) return;
      const nodes = result.crumbs.map((crumb, index) => {
        const item = doc.createElement('li');
        item.className = 'pc-breadcrumb__item';
        if (index === 0) item.setAttribute('data-breadcrumb-home', '');
        if (index) {
          const separator = doc.createElement('span');
          separator.className = 'pc-breadcrumb__separator';
          separator.setAttribute('aria-hidden', 'true');
          separator.textContent = '›';
          item.appendChild(separator);
        }
        const label = doc.createElement(crumb.current ? 'span' : 'a');
        label.textContent = crumb.title;
        if (crumb.current) { label.setAttribute('aria-current', 'page'); item.setAttribute('data-breadcrumb-current', ''); }
        else { label.href = localURL(crumb.url); }
        item.appendChild(label);
        return item;
      });
      list.replaceChildren(...nodes);
      nav.dataset.resolvedSite = result.site;
      const section = nav.closest('.shopify-section') || nav.parentElement;
      let schema = section.querySelector('[data-pc-breadcrumb-schema]');
      if (result.crumbs.length < 2) { if (schema) schema.remove(); return; }
      if (!schema) {
        schema = doc.createElement('script');
        schema.type = 'application/ld+json';
        schema.setAttribute('data-pc-breadcrumb-schema', '');
        section.appendChild(schema);
      }
      schema.textContent = JSON.stringify({
        '@context': 'https://schema.org', '@type': 'BreadcrumbList',
        itemListElement: result.crumbs.map((crumb, index) => ({
          '@type': 'ListItem', position: index + 1, name: crumb.title,
          item: new URL(localURL(crumb.url), win.location.origin).href
        }))
      });
    }
    function refresh() {
      const nextConfig = readJSON(doc.getElementById('pc-navigation-config'));
      if (!nextConfig || !nextConfig.sites) return;
      config = nextConfig;
      const navs = Array.from(doc.querySelectorAll('[data-pc-breadcrumb]'));
      const firstNav = navs[0];
      enabled = !firstNav || firstNav.dataset.sessionEnabled !== 'false';
      const previousState = readState();
      currentData = firstNav ? readJSON(firstNav.querySelector('[data-pc-breadcrumb-data]')) || {} : {};
      currentData.minutes = firstNav && firstNav.dataset.sessionMinutes;
      const sameDocument = active && active.path === pathKey(win.location.href, win.location.origin);
      if (sameDocument) {
        currentData.documentSite = active.site;
        currentData.documentCollection = active.collection && (active.collection.id || active.collection.handle);
      }
      active = resolve(config, currentData, win.location, doc.referrer, previousState, Date.now(), enabled);
      for (const nav of navs) {
        const data = readJSON(nav.querySelector('[data-pc-breadcrumb-data]')) || {};
        data.minutes = nav.dataset.sessionMinutes;
        data.documentSite = active.site;
        data.documentCollection = active.collection && (active.collection.id || active.collection.handle);
        render(nav, resolve(config, data, win.location, doc.referrer, previousState, Date.now(), enabled));
      }
      writeState({ version: 2, site: active.site, path: active.path, updatedAt: Date.now(),
        pageType: config.pageType, resourceId: config.resourceId, collectionId: active.collection && active.collection.id });
      try { win.sessionStorage.removeItem('pepiniereBreadcrumbContext'); } catch (_) { }
    }
    function rememberLink(event) {
      if (!active || !enabled || event.defaultPrevented || event.button > 0 || event.altKey) return;
      const link = event.target.closest && event.target.closest('a[href]');
      if (!link || link.hasAttribute('download')) return;
      const url = parseURL(link.getAttribute('href'), win.location.origin);
      if (!isStoreURL(url, config, win.location.origin)) return;
      const to = pathKey(url.href, win.location.origin);
      if (to === active.path) return;
      const site = explicitSite(url.href, config, win.location.origin) || active.site;
      writeState({
        version: 2, site: active.site, path: active.path, updatedAt: Date.now(),
        pageType: config.pageType, resourceId: config.resourceId, collectionId: active.collection && active.collection.id,
        intent: { from: active.path, to, site, collection: active.collection && active.collection.handle, collectionId: active.collection && active.collection.id, at: Date.now() }
      });
    }
    doc.addEventListener('click', rememberLink);
    doc.addEventListener('shopify:section:load', refresh);
    win.addEventListener('pageshow', event => { if (event.persisted) refresh(); });
    win.PepiniereBreadcrumb = { refresh };
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', refresh, { once: true });
    else refresh();
  }
  return { pathKey, explicitSite, validState, resolve, start };
});
